import * as env from '~/server_env';
import ignoreErrors from './ignoreErrors';

export const Severity = {
  Critical: 'critical',
  Fatal: 'fatal',
  Error: 'error',
  Warning: 'warning',
  Info: 'info',
  Log: 'log',
  Debug: 'debug',
};

/**
 * Wrapper around Sentry itself that will allow us to lazy load Sentry when the first
 * error takes place. We won't lose any errors since we're adding them into a queue
 * to be processed later when Sentry has been attached.
 */
export default class HubstaffErrorReporter {
  queuedErrors = [];
  loading = undefined; // undefined, true, false

  // @type {import('@sentry/browser')}
  sentry = null;

  // @type {import('@sentry/vue')}
  sentryVue = null;

  // Vue apps registered via `attachToVueApp`; handed to @sentry/vue's handler once Sentry loads.
  vueApps = [];

  constructor() {
    // Force `reportError` to always point to the instance!!!
    // We need this because we're attaching us to the `window` as a global helper
    // and `this` will not point correctly otherwise.
    this.reportError = this.reportError.bind(this);

    // Manually set error handling at the global scope so that we can then
    // lazy load sentry when the first error occurs.
    window.onerror = this.reportError;
  }

  async load() {
    try {
      this.loading = true;
      // Core SDK from @sentry/browser; the Vue-3 error handler from @sentry/vue. We init via
      // @sentry/browser instead of @sentry/vue's `init({ app })` because Sentry is lazy-loaded long
      // after the apps mount, and `init({ app })` warns that the app is "already mounted".
      const [Sentry, SentryVue] = await Promise.all([import('@sentry/browser'), import('@sentry/vue')]);

      this.sentry = Sentry;
      this.sentryVue = SentryVue;
      this.loading = false;

      // Clear out the manual error handling from the window, Sentry
      // will add its hooks in order to handle exceptions.
      window.onerror = undefined;

      // Get the host for the app, make sure we only get a string
      // with the form of `app.hubstaff.com`.
      const url = new URL(env.APP_HOST);
      const host = url.host;
      let allowUrls = { allowUrls: [new RegExp(host)] };

      if (import.meta.env.DEV) {
        allowUrls = {};
      }

      this.sentry.init({
        debug: import.meta.env.DEV,
        dsn: env.SENTRY_DSN,
        integrations: [this.sentry.browserTracingIntegration()],
        tracesSampleRate: env.SENTRY_TRACES_SAMPLE_RATE,
        sampleRate: env.SENTRY_SAMPLE_RATE,
        release: env.RELEASE_NAME,
        environment: env.SENTRY_ENVIRONMENT,
        autoSessionTracking: true,
        denyUrls: [/file:\//i, /^chrome:\/\//i, /extension:\/\//i],
        ...allowUrls,
        ignoreErrors,
        beforeSend(event, hint) {
          const status = hint?.originalException?.params?.status;

          // Validation/bad-request responses are handled by the UI; they are not actionable
          // as Sentry issues and drown out real errors.
          if (status === 400 || status === 422) {
            return null; // drop, don't send
          }

          return event;
        },
      });

      this.#setUser();
      // Hand every app collected before load over to @sentry/vue's Vue-3 error handler.
      this.vueApps.forEach((app) => this.#attachSentryErrorHandler(app));
      this.#processQueuedErrors();
    } catch (err) {
      console.error('Unable to load/init sentry', err);
    }
  }

  reportError(error, context = {}, severity = Severity.Error) {
    // `context` can be an empty string, which won't default to `{}`
    // in the default parameters initialization.
    context ||= {};
    severity = this.#getSeverity(severity);
    context = typeof context === 'string' ? { url: context } : context;

    if (this.loading === undefined) {
      // First hit...
      this.queuedErrors.push({ error, context, severity });
      this.load();
    } else if (this.loading === true) {
      // Hit if we try to report but we have not yet finished loading Sentry.
      this.queuedErrors.push({ error, context, severity });
    } else {
      // Everything is loaded, go ahead and delegate to `#doErrorReport`.
      this.#doErrorReport(error, context, severity);
    }
  }

  attachToVueApp(app) {
    this.vueApps.push(app);

    // Sentry is lazy-loaded on the first error. If it's already loaded (e.g. a second app mounts
    // afterwards), wire up its handler now. Otherwise install a lightweight handler that queues the
    // error and triggers the load; `load()` then swaps every app over to @sentry/vue's handler.
    if (this.sentryVue) {
      this.#attachSentryErrorHandler(app);

      return;
    }

    app.config.errorHandler = (error, vm, lifecycleHook) => {
      this.reportError(error, { lifecycleHook, propsData: vm?.$props });
      // Keep the error visible in the console until @sentry/vue's handler (which logs) takes over.
      if (!app.config.silent) {
        console.error(error);
      }
    };
  }

  // =======
  // Private
  // =======

  #attachSentryErrorHandler(app) {
    // Drop our lazy bootstrap handler first so @sentry/vue's handler doesn't chain-call it.
    app.config.errorHandler = undefined;
    this.sentryVue.attachErrorHandler(app, { attachProps: true, logErrors: true });
  }

  #setUser() {
    let userDetails = {};

    if (document.body && document.body.dataset && document.body.dataset.currentUserDetails) {
      userDetails = JSON.parse(document.body.dataset.currentUserDetails);
    }

    this.sentry.setUser(userDetails);
  }

  #processQueuedErrors() {
    // Process any queued errors...
    this.queuedErrors.forEach(({ error, context, severity }) => {
      this.#doErrorReport(error, context, severity);
    });
  }

  #doErrorReport(error, context = {}, severity = Severity.Error) {
    // Capture exception in the next event loop, to make sure that all breadcrumbs are recorded in time.
    queueMicrotask(() => {
      this.sentry.withScope((scope) => {
        scope.setLevel(severity);

        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value);
        });

        this.sentry.captureException(error);
      });
    });
  }

  #getSeverity(severity) {
    return (
      {
        [Severity.Critical]: 'critical',
        [Severity.Fatal]: 'fatal',
        [Severity.Error]: 'error',
        [Severity.Warning]: 'warning',
        [Severity.Info]: 'info',
        [Severity.Log]: 'log',
        [Severity.Debug]: 'debug',
      }[severity] || 'error'
    );
  }
}
